import { Grid, MenuItem, Stack, Typography } from "@mui/material";
import { Label } from "./SelectAccountType";
import AddressLookUpComponent, { StyledEndAdorment } from "./AddressLookup";
import { FileUpload } from "./FileUploadField";
import { Field, useFormikContext } from "formik";
import { StyledTextField } from "./styled-components/AddressFields";
import { FormValues } from "./utils/formSchema";
import useCompanyFormConfig from "./utils/useAboutYourEntityConfig";
import { useEffect } from "react";
import { useAppDispatch } from "src/store/store";
import {
  setEntityId,
  setEntityName,
  setCompanyType,
  setNatureOfBusiness,
  setOtherCompanyType,
} from "src/store/features/onboarding.slice";
import { EntityDetailsDto } from "src/store/features/api/generated";
import { ArrowDropDown } from "@mui/icons-material";
import { HtmlTooltip } from "../trade/target-orders/tooltips/TargetOrderProjectionsToolTip";
import InfoIconStyled from "src/icons/InfoIconStyled";

export default function AboutYourEntity() {
  const {
    nameLabel,
    idLabel,
    addressLabel,
    uploadInstructions,
    recordType,
    companyType: reduxCompanyType,
    enableNatureOfBusiness,
  } = useCompanyFormConfig();

  const {
    values: { entityName, entityId, country, natureOfBusiness, otherCompanyType, companyType },
    errors: {
      entityName: entityNameError,
      entityId: entityIdError,
      companyType: companyTypeError,
      natureOfBusiness: natureOfBusinessError,
    },
  } = useFormikContext<FormValues>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setEntityId(entityId));
    dispatch(setEntityName(entityName));
    if (country === "Australia") {
      dispatch(setCompanyType("Proprietary Limited Company (Pty Ltd)"));
    } else if (country !== "") {
      dispatch(setCompanyType(companyType as EntityDetailsDto["companyType"]));
    }
    dispatch(setOtherCompanyType(otherCompanyType));
    dispatch(setNatureOfBusiness(natureOfBusiness));
  }, [entityName, entityId, companyType, otherCompanyType, natureOfBusiness, country]);

  const notAustralia = country !== "Australia";
  const nonIndividualTypeIsCompany = recordType === "Other";
  const contryIsNotNull = country !== "";
  const recordTypeIsNotCompany = recordType !== "Other";

  const shouldRequestCompanyType = notAustralia && nonIndividualTypeIsCompany && contryIsNotNull;

  const companyOptions: EntityDetailsDto["companyType"][] = [
    "Other",
    "Limited Liability Company (LLC)",
    "Proprietary Limited Company (Pty Ltd)",
    "Corporation",
    "Partnership",
  ];

  const natureOfBusinessPlaceHolder =
    "Eg. We are involved in the construction industry. We would like part of the company’s wealth to be invested in cryptocurrencies.";

  const natureOfBusinessHelperText =
    "Please describe what the entity does and the industry it operates in where relevant, and the purpose of opening this account.";

  return (
    <Grid container spacing={1} mt={2}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Stack spacing={1}>
            <Label text={nameLabel} />
            <Field
              name="entityName"
              as={StyledTextField}
              selected={!!entityName}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!entityName} />,
              }}
              error={!!entityNameError}
              helperText={entityNameError}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack spacing={1}>
            <Label text={idLabel} />
            <Field
              name="entityId"
              as={StyledTextField}
              error={!!entityIdError}
              helperText={entityIdError}
              selected={!!entityId}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!entityId} />,
              }}
            />
          </Stack>
        </Grid>
        {enableNatureOfBusiness && (
          <Grid item xs={12}>
            <Stack spacing={1}>
              <Stack direction={"row"} spacing={1}>
                <Label text={"Nature of Business"} />
                <HtmlTooltip title={natureOfBusinessHelperText}>
                  <div>
                    <InfoIconStyled />
                  </div>
                </HtmlTooltip>
              </Stack>
              <Field
                name="natureOfBusiness"
                placeholder={natureOfBusinessPlaceHolder}
                as={StyledTextField}
                error={!!natureOfBusinessError}
                helperText={natureOfBusinessError}
                selected={!!natureOfBusiness && !natureOfBusinessError}
                InputProps={{
                  endAdornment: <StyledEndAdorment selected={!!natureOfBusiness} />,
                }}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Stack>
          </Grid>
        )}

        <Grid item xs={12}>
          <Stack mb={1}>
            <Label text={addressLabel} />
          </Stack>
          <Stack>
            <AddressLookUpComponent />
          </Stack>
        </Grid>
        {shouldRequestCompanyType && (
          <>
            <Grid item xs={12}>
              <Label text="Company Type" />
            </Grid>
            <Grid item xs={12} mb={2}>
              <Field
                as={StyledTextField}
                label={"Select an item"}
                select
                name="companyType"
                selected={!!companyType}
                error={!!companyTypeError}
                helperText={companyTypeError}
                required={true}
                variant={"outlined"}
                SelectProps={{
                  IconComponent: () => (
                    <div style={{ display: !!companyType ? "none" : "flex" }}>
                      <ArrowDropDown />
                    </div>
                  ),
                }}
                InputProps={{
                  endAdornment: <StyledEndAdorment selected={!!companyType} />,
                }}>
                {companyOptions
                  .filter((item) => {
                    // if country is !Australia remove Properietary Limited Company (Pty Ltd)
                    if (country !== "Australia") {
                      return item !== "Proprietary Limited Company (Pty Ltd)";
                    }
                    return false;
                  })
                  .map((item, it) => (
                    <MenuItem
                      key={item ?? "" + it}
                      value={item}
                      sx={{ display: item ? "block" : "none" }}>
                      <Typography sx={{ textTransform: "capitalize" }}>{item}</Typography>
                    </MenuItem>
                  ))}
                <MenuItem sx={{ display: "none" }} value=""></MenuItem>
              </Field>
            </Grid>
            {companyType === "Other" && (
              <Grid item xs={12}>
                <Field
                  as={StyledTextField}
                  name={"otherCompanyType"}
                  label={"Please specify your company type"}
                  selected={!!otherCompanyType}
                  InputProps={{
                    endAdornment: <StyledEndAdorment selected={!!otherCompanyType} />,
                  }}
                />
              </Grid>
            )}
          </>
        )}
        {(!!reduxCompanyType || recordTypeIsNotCompany) && (
          <Grid item xs={12} mt={2}>
            <Label text="Upload Documents" />
            <ul>
              {uploadInstructions["firstLine"].map((instruction, key) => (
                <li key={key}>{instruction}</li>
              ))}
              <li>The document you provide must include information on:</li>
              <ul>
                {uploadInstructions["secondLine"].map((instruction, key) => (
                  <li key={key}>{instruction}</li>
                ))}
              </ul>
            </ul>
            <FileUpload documentType={"Entity"} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
